import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { exibirCodRelatorio, fmtMesAno, formatarCnpj } from "../utils";
import { STATUS_APURACAO } from "../constants";
import styles from "./ModalStatusDetalhe.module.css";

const NATUREZA_LABEL = { erro: "Erro", divergencia: "Divergência" };
const ORIGEM_LABEL = { job: "Processamento", sumarizacao: "Sumarização" };

function chipNaturezaSx(natureza) {
  if (natureza === "erro") {
    return { bgcolor: "#ffebee", color: "#c62828", fontWeight: 700 };
  }
  return { bgcolor: "#fff8e1", color: "#8d6e00", fontWeight: 700 };
}

function chipOrigemSx() {
  return {
    bgcolor: "#eef2f9",
    color: "#18316e",
    fontWeight: 600,
    border: "1px solid #d5dbe8",
  };
}

/**
 * TXM-325 item 06 (Parte B) — modal aberta ao clicar em "Histórico / processamento",
 * estruturando `status_detalhe` (item 07); CTA "Ver fila" leva à tela de processamento
 * filtrada por apuracao_id + cnpj (item 06 Parte A).
 */
export default function ModalStatusDetalhe({ open, onClose, row, onVerProcessamento }) {
  const statusDetalhe =
    row?.statusDetalhe && typeof row.statusDetalhe === "object" ? row.statusDetalhe : {};
  const itens = Array.isArray(statusDetalhe.itens) ? statusDetalhe.itens : [];
  const cfg = STATUS_APURACAO[row?.status] || STATUS_APURACAO.pendente;
  const periodoLabel =
    row?.periodoInicio || row?.periodoFim
      ? `${fmtMesAno(row?.periodoInicio)} – ${fmtMesAno(row?.periodoFim)}`
      : "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: styles.dialogPaper }}
    >
      <DialogTitle className={styles.header}>
        <div className={styles.headerTitle}>
          <HistoryOutlinedIcon className={styles.headerIcon} />
          <Typography component="h2" className={styles.title}>
            Processamento
          </Typography>
        </div>
        <Chip
          label={cfg.label}
          size="small"
          sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: "bold", flexShrink: 0 }}
        />
      </DialogTitle>

      <DialogContent className={styles.content}>
        <div className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>CNPJ</span>
            <div className={styles.metaValue}>{formatarCnpj(row?.cnpj)}</div>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Razão social</span>
            <div className={styles.metaValue}>{row?.empresa || "—"}</div>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Competência</span>
            <div className={styles.metaValue}>{periodoLabel}</div>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Tipo</span>
            <div className={styles.metaValue}>{exibirCodRelatorio(row?.tipo)}</div>
          </div>
        </div>

        {statusDetalhe.resumo ? (
          <Alert severity="info" className={styles.resumo} icon={<HistoryOutlinedIcon />}>
            {statusDetalhe.resumo}
          </Alert>
        ) : null}

        {itens.length === 0 ? (
          <div className={styles.emptyState}>
            <InboxOutlinedIcon className={styles.emptyIcon} />
            <Typography className={styles.emptyText}>
              Nenhum detalhe registrado para esta apuração.
            </Typography>
          </div>
        ) : (
          <ul className={styles.timeline}>
            {itens.map((item, idx) => {
              const isErro = item.natureza === "erro";
              const cardClass = [
                styles.eventCard,
                isErro ? styles.eventCardErro : styles.eventCardDivergencia,
              ].join(" ");
              const railClass = [
                styles.eventRail,
                isErro ? styles.eventRailErro : styles.eventRailDivergencia,
              ].join(" ");

              return (
                <li key={idx} className={cardClass}>
                  <span className={railClass} aria-hidden />
                  <div className={styles.eventBody}>
                    <div className={styles.eventTop}>
                      <Chip
                        label={ORIGEM_LABEL[item.origem] || item.origem}
                        size="small"
                        sx={chipOrigemSx()}
                      />
                      <Chip
                        label={NATUREZA_LABEL[item.natureza] || item.natureza}
                        size="small"
                        sx={chipNaturezaSx(item.natureza)}
                      />
                      {item.competencia ? (
                        <Chip
                          label={item.competencia}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : null}
                    </div>
                    <Typography className={styles.eventMsg}>{item.mensagem}</Typography>
                    {item.codigo ? (
                      <div className={styles.eventMeta}>Código: {item.codigo}</div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button onClick={onClose} variant="outlined" className={styles.btnFechar}>
          Fechar
        </Button>
        <Button onClick={onVerProcessamento} variant="contained" className={styles.btnVerFila}>
          Ver fila
        </Button>
      </DialogActions>
    </Dialog>
  );
}
